import { Currency, Money } from '@principle-theorem/accounting';
import {
  DEPOSIT_STAFF_ATTENTION_REASON,
  DepositCaptureStrategy,
  DepositPaymentMode,
  DepositRequirementSource,
  DepositStatus,
  DepositUnresolvedReason,
  type DepositStaffAttentionReason,
  type IDepositMetadata,
} from '@principle-theorem/principle-core/interfaces';
import { type Currency as CurrencyCode } from 'dinero.js';
import { compact } from 'lodash';
import {
  DEPOSIT_CAPTURE_STRATEGY_FALLBACK_DESCRIPTIONS,
  DEPOSIT_CAPTURE_STRATEGY_FALLBACK_LABELS,
  DEPOSIT_CAPTURE_STRATEGY_LABELS,
  DEPOSIT_CAPTURE_STRATEGY_TOOLTIPS,
} from '../../appointment-view-sidebar/appointment-deposit-sidebar/deposit-capture-strategy-display';
import {
  DEPOSIT_SOURCE_LABELS,
  DEPOSIT_SOURCE_TOOLTIPS,
} from '../../appointment-view-sidebar/appointment-deposit-sidebar/deposit-source-display';
import { DEPOSIT_STATUS_COLOURS } from '../../appointment-view-sidebar/appointment-deposit-sidebar/deposit-status-display';

/**
 * `Attention` marks something a person has to act on; `Detail` is background.
 * Tooltip sections use it to lead with the actionable fact and to colour its
 * heading.
 */
export enum DepositChipTone {
  Attention = 'attention',
  Detail = 'detail',
}

/**
 * One block of the chip's tooltip: a heading and the detail beneath it.
 *
 * Sections stay separate rather than being joined into a sentence because a
 * deposit can carry three unrelated facts at once — something needs doing, the
 * strategy did not apply, and what the strategy was. Run together they read as
 * one undifferentiated paragraph with no clue which part is actionable.
 */
export interface IDepositChipTooltipSection {
  readonly tone: DepositChipTone;
  readonly icon?: string;
  readonly heading?: string;
  readonly body: string;
}

/**
 * Everything the deposit chip renders. Derived wholly from a deposit's own
 * fields so that any surface holding an `IDepositMetadata` renders an
 * identical chip without repeating the copy or the colour decisions.
 */
export interface IDepositChipState {
  readonly colourClasses: string;
  readonly label: string;
  /**
   * Answers whether the card is being held or charged. Omitted only where no
   * deposit exists to have a mechanism.
   */
  readonly icon?: string;
  /** Formatted money. Omitted when no amount applies to the state. */
  readonly amount?: string;
  /** The whole story on hover. Empty where there is nothing more to say. */
  readonly tooltipSections: readonly IDepositChipTooltipSection[];
  /**
   * Icon names rendered at the chip's right edge, flagging that there is
   * more to read. Undecorated: the icon itself carries the meaning, and the
   * detail lives in `tooltipSections`.
   */
  readonly markers: readonly string[];
}

/** Why a row has no deposit metadata to describe. */
export enum DepositAbsenceKind {
  NotRequired = 'notRequired',
  PatientRequires = 'patientRequires',
  Unresolved = 'unresolved',
  /**
   * A deposit is required but nothing has been asked of the patient yet. The
   * scheduling form is in this state until the appointment is saved.
   */
  NotRequested = 'notRequested',
  /**
   * What a booking would ask for, shown before there is a booking at all,
   * such as against each option in the treatment picker. Carries the payment
   * mode so staff can tell a card hold from a charge while they choose.
   */
  Required = 'required',
}

export interface IDepositAbsence {
  readonly kind: DepositAbsenceKind;
  /** Only meaningful when {@link IDepositAbsence.kind} is `Unresolved`. */
  readonly unresolvedReason?: DepositUnresolvedReason;
  /**
   * Only meaningful when {@link IDepositAbsence.kind} is `NotRequested` or
   * `Required`: what will be asked for. Omitted where the host already shows
   * the amount.
   */
  readonly amountCents?: number;
  readonly currency?: CurrencyCode;
  /**
   * The payment mode picks the hold or charge icon for `NotRequested` and
   * `Required`. The strategy and source only apply to `Required`, where they
   * explain the deposit on hover.
   */
  readonly paymentMode?: DepositPaymentMode;
  readonly captureStrategy?: DepositCaptureStrategy;
  readonly source?: DepositRequirementSource;
}

/**
 * The one chip with no money state to colour by: a deposit that was required
 * but could not be taken. Red here is the whole message, not an overlay.
 */
export const DEPOSIT_UNRESOLVED_COLOUR_CLASSES =
  'border border-red-200 bg-red-100 text-red-800';

/**
 * What the tooltip says about a flagged deposit: the heading names what went
 * wrong, the body says why it happened and what to do about it.
 *
 * The heading is specific rather than a generic "needs attention" because
 * staff scanning a column need to know at a glance whether a flag is a lapsed
 * hold, a failed refund or a booking nobody dealt with. Those call for quite
 * different next steps.
 */
export interface IDepositAttentionText {
  readonly heading: string;
  readonly body: string;
}

export const DEPOSIT_ATTENTION_TEXT: Record<
  DepositStaffAttentionReason,
  IDepositAttentionText
> = {
  [DEPOSIT_STAFF_ATTENTION_REASON.UnactionedLiveMoney]: {
    heading: 'Booking date passed without a decision',
    body: 'Money was taken from the patient with this booking request, but the appointment time has passed and nobody accepted or declined the request. Accept it to keep the appointment, or decline it to release or refund the deposit.',
  },
  [DEPOSIT_STAFF_ATTENTION_REASON.ReleaseFailed]: {
    heading: 'Card hold could not be released',
    body: 'This hold should have been released back to the patient, but the payment provider refused the release. Release it manually so the patient is not left out of pocket.',
  },
  [DEPOSIT_STAFF_ATTENTION_REASON.RefundCreditShortfall]: {
    heading: 'Refund fell short',
    body: 'The refund could not be completed in full because the credit left on the deposit invoice was less than the deposit. Review the invoice and refund the remainder.',
  },
  [DEPOSIT_STAFF_ATTENTION_REASON.RefundFailed]: {
    heading: 'Refund failed',
    body: 'The payment provider could not return the money to the patient. Check the provider’s failure reason and arrange the refund and any connected-account transfer manually.',
  },
  [DEPOSIT_STAFF_ATTENTION_REASON.HoldCannotCoverRescheduledAppointment]: {
    heading: 'Card hold ends before the new time',
    body: 'The appointment was moved to a date after the card hold expires, so the hold can no longer cover it. Take a new deposit for the new time.',
  },
  [DEPOSIT_STAFF_ATTENTION_REASON.HoldLapsedBeforeAppointment]: {
    heading: 'Card hold lapsed',
    body: 'The card hold expired before the appointment took place, so nothing can be captured from it. Take a new deposit.',
  },
};

export const DEPOSIT_UNRESOLVED_DESCRIPTIONS: Record<
  DepositUnresolvedReason,
  string
> = {
  [DepositUnresolvedReason.MissingTreatmentCost]:
    'A deposit was required but the treatment has no price at this practice, so a percentage could not be calculated. No deposit was taken.',
  [DepositUnresolvedReason.MissingDepositAmount]:
    'A deposit was required but no amount is configured. No deposit was taken.',
};

const HOLD_ICON = 'lock';
const CHARGE_ICON = 'payments';
const OVERRIDDEN_ICON = 'edit';

/**
 * Explains an amount that staff typed in rather than one the settings
 * produced. Without this a deposit that matches no current rule looks like a
 * bug; with it, the receptionist knows there is no setting to go and fix.
 */
const amountOverriddenSection: IDepositChipTooltipSection = {
  tone: DepositChipTone.Detail,
  icon: OVERRIDDEN_ICON,
  heading: 'Amount set by staff',
  body: 'The configured amount was changed by hand when this appointment was booked, so the deposit settings will not explain it.',
};

/**
 * Cancellation cover once the money has actually been taken.
 *
 * The configured strategy is still cancellation cover, so the deposit behaves
 * as one: kept only if the patient cancels late. But the strategy's own copy
 * describes a card hold that gets released on attendance, and a deposit staff
 * recorded as a payment has no hold to release. Read against a `Paid` chip
 * that sentence is simply untrue, which is what makes an ordinary payment look
 * like it is being held.
 */
const cancellationCoverPaidSection: IDepositChipTooltipSection = {
  tone: DepositChipTone.Detail,
  heading: 'Kept as cancellation cover',
  body: 'This deposit was taken as a payment rather than held on the card, so there is no hold to release. It sits as a credit against the appointment, and is only kept as a cancellation fee if the patient cancels or reschedules late.',
};

/**
 * What staff read on a deposit that is not `Authorised`. Each names the money's
 * resting place rather than the mechanism, because for these statuses the
 * capture strategy has either not applied yet or has already played out.
 */
const statusLabels: Record<DepositStatus, string> = {
  [DepositStatus.Required]: 'Not requested',
  [DepositStatus.Pending]: 'Awaiting payment',
  [DepositStatus.Authorised]: 'Held',
  [DepositStatus.Paid]: 'Paid',
  [DepositStatus.Forfeited]: 'Kept as cancellation fee',
  [DepositStatus.Refunded]: 'Refunded',
  [DepositStatus.Waived]: 'Waived',
  [DepositStatus.Cancelled]: 'Cancelled',
  [DepositStatus.Released]: 'Released',
};

/**
 * What staff read on an `Authorised` deposit. This is the one status where the
 * capture strategy still decides the outcome, so the label answers "what
 * happens to this hold" rather than repeating that a hold exists.
 *
 * `ChargeOnBookingRequest` captures immediately and so never authorises, but
 * the map stays total to force a decision when a strategy is added.
 */
const authorisedLabels: Record<DepositCaptureStrategy, string> = {
  [DepositCaptureStrategy.ChargeOnBookingRequest]: 'Held',
  [DepositCaptureStrategy.CaptureOnBookingRequestApproval]:
    'Held, charge on accept',
  [DepositCaptureStrategy.CaptureOnCancellationOrReschedule]:
    'Held, cancellation cover',
};

/**
 * Statuses where the money has already moved, so the icon reports what happened
 * rather than forecasting from the payment mode. Deliberately partial:
 * `Required` and `Pending` fall through to the mode, since for them the hold or
 * charge is still ahead.
 */
const settledIcons: Partial<Record<DepositStatus, string>> = {
  [DepositStatus.Authorised]: HOLD_ICON,
  [DepositStatus.Paid]: CHARGE_ICON,
  [DepositStatus.Forfeited]: CHARGE_ICON,
  [DepositStatus.Released]: 'lock_open',
  [DepositStatus.Refunded]: 'undo',
  [DepositStatus.Waived]: 'block',
  [DepositStatus.Cancelled]: 'cancel',
};

const notRequiredState: IDepositChipState = {
  colourClasses: 'border border-gray-200 bg-gray-100 text-gray-800',
  label: 'Not required',
  tooltipSections: [],
  markers: [],
};

const patientRequiresState: IDepositChipState = {
  colourClasses: DEPOSIT_STATUS_COLOURS[DepositStatus.Required],
  icon: CHARGE_ICON,
  label: 'Patient requires',
  tooltipSections: [
    {
      tone: DepositChipTone.Detail,
      body: 'This patient is flagged as deposit required. A deposit must be recorded or waived before the appointment can be saved.',
    },
  ],
  markers: [],
};

/**
 * Builds the single chip that stands in for a deposit wherever one is listed.
 *
 * Collapses status, capture strategy, fallback reason and attention flag into
 * one chip, on the rule that the strategy is only worth showing while a hold's
 * fate is still undecided. Everywhere else the strategy has either not applied
 * yet or already resolved, so naming it only competes with the status.
 */
export class DepositChipDisplay {
  /**
   * The chip for a deposit that exists. The colour always follows the status,
   * so a paid deposit reads as paid on every surface. An attention flag adds a
   * red marker and leads the tooltip rather than recolouring the chip: staff
   * need both facts, where the money is and that someone has to deal with it,
   * and a red background hid the first behind the second.
   */
  static forMetadata(deposit: IDepositMetadata): IDepositChipState {
    return {
      colourClasses: DEPOSIT_STATUS_COLOURS[deposit.status],
      icon: DepositChipDisplay._icon(deposit),
      label: DepositChipDisplay._label(deposit),
      amount: DepositChipDisplay._amount(deposit),
      tooltipSections: DepositChipDisplay._tooltipSections(deposit),
      markers: DepositChipDisplay._markers(deposit),
    };
  }

  /**
   * The chip for a row with no deposit metadata. These read as ordinary chips
   * rather than bare text so a column of deposits has one visual language.
   */
  static forAbsence(absence: IDepositAbsence): IDepositChipState {
    switch (absence.kind) {
      case DepositAbsenceKind.NotRequired:
        return notRequiredState;
      case DepositAbsenceKind.PatientRequires:
        return patientRequiresState;
      case DepositAbsenceKind.Unresolved:
        return DepositChipDisplay._unresolvedState(absence.unresolvedReason);
      case DepositAbsenceKind.NotRequested:
        return DepositChipDisplay._notRequestedState(absence);
      case DepositAbsenceKind.Required:
        return DepositChipDisplay._requiredState(absence);
      default: {
        const exhaustiveKind: never = absence.kind;
        void exhaustiveKind;
        throw new Error('Unexpected deposit absence kind');
      }
    }
  }

  private static _label(deposit: IDepositMetadata): string {
    if (deposit.status !== DepositStatus.Authorised) {
      return statusLabels[deposit.status];
    }
    const captureStrategy = deposit.captureStrategy;
    return captureStrategy
      ? authorisedLabels[captureStrategy]
      : statusLabels[DepositStatus.Authorised];
  }

  private static _icon(deposit: IDepositMetadata): string {
    const settledIcon = settledIcons[deposit.status];
    if (settledIcon) {
      return settledIcon;
    }
    return DepositChipDisplay._paymentModeIcon(deposit.paymentMode);
  }

  /** Hold or charge, for a deposit whose money has not moved yet. */
  private static _paymentModeIcon(paymentMode?: DepositPaymentMode): string {
    return paymentMode === DepositPaymentMode.PreAuth ? HOLD_ICON : CHARGE_ICON;
  }

  private static _amount(deposit: IDepositMetadata): string | undefined {
    return DepositChipDisplay._formatAmount(
      deposit.amountCents,
      deposit.currency
    );
  }

  private static _formatAmount(
    amountCents?: number,
    currency?: CurrencyCode
  ): string | undefined {
    if (amountCents === undefined || amountCents <= 0) {
      return;
    }
    return Money.formatPlainCents(amountCents, currency ?? Currency.AUD);
  }

  /**
   * Attention leads, being the only section anyone has to act on. Why the
   * deposit exists comes before how it is captured, since that is the question
   * staff ask first when an amount looks wrong.
   */
  private static _tooltipSections(
    deposit: IDepositMetadata
  ): IDepositChipTooltipSection[] {
    return compact([
      DepositChipDisplay._attentionSection(deposit),
      DepositChipDisplay._fallbackSection(deposit),
      DepositChipDisplay._sourceSection(deposit),
      deposit.amountOverridden ? amountOverriddenSection : undefined,
      DepositChipDisplay._metadataStrategySection(deposit),
    ]);
  }

  /**
   * `Paid` is the one status that contradicts the cancellation cover copy:
   * `Authorised` still holds the card, `Released` and `Refunded` describe a
   * hold that ran its course, and `Required` and `Pending` are forecasts of
   * one. Only here has the money been taken outright with no hold involved.
   */
  private static _metadataStrategySection(
    deposit: IDepositMetadata
  ): IDepositChipTooltipSection | undefined {
    const isPaidCancellationCover =
      deposit.status === DepositStatus.Paid &&
      deposit.captureStrategy ===
        DepositCaptureStrategy.CaptureOnCancellationOrReschedule;
    return isPaidCancellationCover
      ? cancellationCoverPaidSection
      : DepositChipDisplay._strategySection(deposit);
  }

  /** Only deposits saved since the source was recorded can say why. */
  private static _sourceSection(
    origin: Pick<IDepositMetadata, 'source'>
  ): IDepositChipTooltipSection | undefined {
    const source = origin.source;
    if (!source || source === DepositRequirementSource.None) {
      return;
    }
    return {
      tone: DepositChipTone.Detail,
      heading: `Required for ${DEPOSIT_SOURCE_LABELS[source]}`,
      body: DEPOSIT_SOURCE_TOOLTIPS[source],
    };
  }

  private static _attentionSection(
    deposit: IDepositMetadata
  ): IDepositChipTooltipSection | undefined {
    const attention = deposit.attention;
    if (!attention) {
      return;
    }
    const text = DEPOSIT_ATTENTION_TEXT[attention.reason];
    return {
      tone: DepositChipTone.Attention,
      icon: 'warning',
      heading: text.heading,
      body: text.body,
    };
  }

  private static _fallbackSection(
    deposit: IDepositMetadata
  ): IDepositChipTooltipSection | undefined {
    const fallbackReason = deposit.fallbackReason;
    if (!fallbackReason) {
      return;
    }
    return {
      tone: DepositChipTone.Detail,
      icon: 'info',
      heading: DEPOSIT_CAPTURE_STRATEGY_FALLBACK_LABELS[fallbackReason],
      body: DEPOSIT_CAPTURE_STRATEGY_FALLBACK_DESCRIPTIONS[fallbackReason],
    };
  }

  private static _strategySection(
    origin: Pick<IDepositMetadata, 'captureStrategy'>
  ): IDepositChipTooltipSection | undefined {
    const captureStrategy = origin.captureStrategy;
    if (!captureStrategy) {
      return;
    }
    return {
      tone: DepositChipTone.Detail,
      heading: DEPOSIT_CAPTURE_STRATEGY_LABELS[captureStrategy],
      body: DEPOSIT_CAPTURE_STRATEGY_TOOLTIPS[captureStrategy],
    };
  }

  /**
   * Only a flag someone has to act on earns a mark on the chip face.
   *
   * A strategy fallback and a staff-set amount are both background: they
   * explain the deposit rather than ask anything of the reader, so they stay
   * in the tooltip. A staff-set amount previously carried a pencil, which read
   * as an invitation to edit a chip that is not interactive.
   */
  private static _markers(deposit: IDepositMetadata): string[] {
    return compact([deposit.attention ? 'warning' : undefined]);
  }

  /**
   * The scheduling form before save. Reads like the `Required` status it will
   * become, so the chip does not change its language once the appointment
   * exists.
   */
  private static _notRequestedState(
    absence: IDepositAbsence
  ): IDepositChipState {
    return {
      colourClasses: DEPOSIT_STATUS_COLOURS[DepositStatus.Required],
      icon: DepositChipDisplay._paymentModeIcon(absence.paymentMode),
      label: statusLabels[DepositStatus.Required],
      amount: DepositChipDisplay._formatAmount(
        absence.amountCents,
        absence.currency
      ),
      tooltipSections: [
        {
          tone: DepositChipTone.Detail,
          body: 'A deposit is required for this appointment but nothing has been asked of the patient yet. Once the appointment is saved it can be recorded at the practice or requested with a payment link.',
        },
      ],
      markers: [],
    };
  }

  /**
   * A deposit a booking would need, before anything is booked. Uses the same
   * hold-or-charge icon, amount and hover copy the saved deposit will show, so
   * staff choosing a treatment see what they are asking of the patient.
   */
  private static _requiredState(absence: IDepositAbsence): IDepositChipState {
    return {
      colourClasses: DEPOSIT_STATUS_COLOURS[DepositStatus.Required],
      icon: DepositChipDisplay._paymentModeIcon(absence.paymentMode),
      label: 'Deposit required',
      amount: DepositChipDisplay._formatAmount(
        absence.amountCents,
        absence.currency
      ),
      tooltipSections: compact([
        DepositChipDisplay._sourceSection(absence),
        DepositChipDisplay._strategySection(absence),
      ]),
      markers: [],
    };
  }

  private static _unresolvedState(
    reason?: DepositUnresolvedReason
  ): IDepositChipState {
    return {
      colourClasses: DEPOSIT_UNRESOLVED_COLOUR_CLASSES,
      icon: 'warning',
      label: 'Not taken',
      tooltipSections: reason
        ? [
            {
              tone: DepositChipTone.Attention,
              icon: 'warning',
              heading: 'Deposit not taken',
              body: DEPOSIT_UNRESOLVED_DESCRIPTIONS[reason],
            },
          ]
        : [],
      markers: [],
    };
  }
}
